// Copyright (c) Docugami, Inc. All rights reserved.

import { useCallback, useMemo } from "react";
import { isValidDecodedTag } from "utils/TagStandardUtils";
import { useDocumentDgml } from "./documentDgml";
import { AddedChunkSchema } from "store/dgml/dgmlTypes";
import { useGetAddedChunkSchemaQuery } from "store/dgml/dgmlSlice";
import { SpanIdUtils } from "utils/SpanIdUtils";
import { createFolderId } from "components/file_management/file_helpers";
import { ChunkInstance } from "networking/api";
import { useUpdateAddedChunkDefinitionMutation, useSetAddedChunkValueMutation, CreateAddedChunkDefinitionRequest, useCreateAddedChunkDefinitionMutation, AddedChunkDefinitionResponse } from "store/frontend/frontendSlice";

export interface Property {
    spanId: string;
    value?: string;
    chunk?: ChunkInstance;
    definitionId: string;
    label: string;
}

interface UpdatePropertyInput {
    label?: string;
    value?: string;
}

interface IDocumentProperties {
    schema?: AddedChunkSchema;
    properties: Property[];
    isLoading: boolean;
    isPropertyValid: (property: Property) => boolean;
    addProperty: (property: Property) => Promise<AddedChunkDefinitionResponse>;
    updateProperty: (targetId: string, input: UpdatePropertyInput) => Promise<void>;
}

export function useDocumentProperties(documentId: string): IDocumentProperties {
    const dgml = useDocumentDgml();

    const folderId = useMemo(() => createFolderId(documentId), [documentId]);

    const { data: schema, isLoading } = useGetAddedChunkSchemaQuery({ docsetId: folderId.clusterId });
    const [createDefinition] = useCreateAddedChunkDefinitionMutation();
    const [updateDefinition] = useUpdateAddedChunkDefinitionMutation();
    const [setValue] = useSetAddedChunkValueMutation();

    const properties = useMemo<Property[]>(() => {
        if (dgml.isInitialized() && !!schema) {
            return schema.definitions.map<Property>(x => {
                const spanId = SpanIdUtils.createAddedChunk(folderId.fileId, x.id);
                const chunk = dgml.getInstanceById(spanId.value) ?? undefined;

                return {
                    spanId: spanId.value,
                    value: chunk?.addedChunkInfo!.value,
                    definitionId: x.id,
                    label: x.label,
                    chunk: chunk
                };
            });
        }

        return [];
    }, [dgml, folderId, schema]);

    const isPropertyValid = useCallback((p: Property): boolean => {
        if (!p.label || !p.value) {
            return false;
        }

        if (!isValidDecodedTag(p.label)) {
            return false;
        }

        const existing = schema?.definitions.find(x => x.id !== p.definitionId && x.label.toLowerCase() === p.label.toLowerCase());
        if (!!existing) {
            return false;
        }

        const current = properties.find(x => x.definitionId === p.definitionId);
        if (current?.value === p.value && current?.label === p.label) {
            return false;
        }

        return true;
    }, [properties, schema?.definitions]);

    const addProperty = useCallback(async (property: Property) => {
        const payload: CreateAddedChunkDefinitionRequest = {
            docsetId: folderId.clusterId,
            label: property.label
        };

        if (!!property.value) {
            payload.instances = [{
                fileId: folderId.fileId,
                value: property.value
            }]
        }

        return await createDefinition(payload).unwrap();
    }, [createDefinition, folderId]);

    const updateProperty = useCallback(async (targetSpanId: string, input: UpdatePropertyInput) => {
        const property = properties.find(x => x.spanId === targetSpanId);
        if (!property) {
            return Promise.resolve();
        }

        const updates = [];
        if (property.value !== input.value && input.value !== undefined) {
            updates.push(setValue({ docsetId: folderId.clusterId, fileId: folderId.fileId, definitionId: property.definitionId, value: input.value ?? '' }))
        }

        if (property.label !== input.label && !!input.label) {
            updates.push(updateDefinition({ docsetId: folderId.clusterId, id: property.definitionId, label: input.label }));
        }

        await Promise.allSettled(updates);
    }, [properties, folderId, setValue, updateDefinition]);

    return {
        properties: properties,
        isLoading: !dgml.isInitialized() || isLoading,
        isPropertyValid: isPropertyValid,
        addProperty: addProperty,
        updateProperty: updateProperty
    }
}
